import type {EstablishedAhpPrompt, EstablishedAhpPromptCommandType} from '@github-ui/ahp-session-client/contracts'
import {
  ChoiceButton,
  ChoiceButtonGroup,
  ChoiceButtonInput,
} from '@github-ui/agent-sessions-interface/components/ChoiceButton'
import {UserChoiceCard} from '@github-ui/agent-sessions-interface/components/UserChoiceCard'
import {Button, TextInput} from '@primer/react'
import {useState, type FocusEventHandler} from 'react'

import {useOptionalEstablishedAhpSessionViewState} from '../hooks/use-established-ahp-session-view'

export interface EstablishedAhpSessionPromptProps {
  prompt: EstablishedAhpPrompt
  disabled?: boolean
  autoFocus?: boolean
  className?: string
  'data-testid'?: string
  onBlurCapture?: FocusEventHandler<HTMLDivElement>
  onFocusCapture?: FocusEventHandler<HTMLDivElement>
  onRespond: (toolCallId: string, response: unknown, commandType: EstablishedAhpPromptCommandType) => boolean | void
}

function recordArguments(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : {}
}

function stringValue(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined
}

/**
 * Shared generic AHP input surface. It intentionally handles only the generic
 * prompt/tool-confirmation convention; product-specific task plan gates stay
 * outside this component.
 */
export function EstablishedAhpSessionPrompt({
  prompt,
  disabled = false,
  autoFocus = false,
  className,
  'data-testid': dataTestId,
  onBlurCapture,
  onFocusCapture,
  onRespond,
}: EstablishedAhpSessionPromptProps) {
  const args = recordArguments(prompt.arguments)
  const [freeform, setFreeform] = useState('')
  const [respondedToolCallId, setRespondedToolCallId] = useState<string>()
  const view = useOptionalEstablishedAhpSessionViewState()
  if (respondedToolCallId && respondedToolCallId !== prompt.toolCallId) {
    setRespondedToolCallId(undefined)
  }
  const isDisabled = disabled || view.passive || respondedToolCallId === prompt.toolCallId
  const question =
    stringValue(args.question) ?? stringValue(args.message) ?? stringValue(args.description) ?? 'Input required'
  const choices = Array.isArray(args.choices)
    ? args.choices.filter((choice): choice is string => typeof choice === 'string')
    : []
  const respond = (response: unknown, commandType: EstablishedAhpPromptCommandType) => {
    if (isDisabled) return
    setRespondedToolCallId(prompt.toolCallId)
    try {
      if (onRespond(prompt.toolCallId, response, commandType) === false) {
        setRespondedToolCallId(undefined)
      }
    } catch (error) {
      setRespondedToolCallId(undefined)
      throw error
    }
  }

  if (prompt.toolName === 'permission_request') {
    return (
      <div className={className} data-testid={dataTestId} onBlurCapture={onBlurCapture} onFocusCapture={onFocusCapture}>
        <UserChoiceCard title={question}>
          <ChoiceButtonGroup aria-label="Permission choices" autoFocus={autoFocus && !isDisabled}>
            <ChoiceButton
              number={1}
              label="Allow"
              ariaLabel="Allow"
              disabled={isDisabled}
              onClick={() =>
                respond(
                  {promptId: prompt.toolCallId, approved: true, scope: 'once', toolDisplayName: question},
                  'permission_response',
                )
              }
            />
            <ChoiceButton
              number={2}
              label="Deny"
              ariaLabel="Deny"
              disabled={isDisabled}
              onClick={() =>
                respond(
                  {promptId: prompt.toolCallId, approved: false, scope: 'once', toolDisplayName: question},
                  'permission_response',
                )
              }
            />
          </ChoiceButtonGroup>
        </UserChoiceCard>
      </div>
    )
  }

  if (prompt.toolName === 'exit_plan_mode') {
    return (
      <div className={className} data-testid={dataTestId} onBlurCapture={onBlurCapture} onFocusCapture={onFocusCapture}>
        <UserChoiceCard
          title={stringValue(args.summary) ?? question}
          subtitle="Plan approval is handled by the established session plan surface."
        />
      </div>
    )
  }

  const submitFreeform = () => {
    if (freeform.length === 0) return
    respond({promptId: prompt.toolCallId, answer: freeform, wasFreeform: true}, 'ask_user_response')
  }

  return (
    <div className={className} data-testid={dataTestId} onBlurCapture={onBlurCapture} onFocusCapture={onFocusCapture}>
      <UserChoiceCard
        title={question}
        footerButtons={
          <>
            <Button
              type="button"
              variant="primary"
              disabled={isDisabled || freeform.length === 0}
              onClick={submitFreeform}
            >
              Submit
            </Button>
            <Button
              type="button"
              disabled={isDisabled}
              onClick={() =>
                respond(
                  {promptId: prompt.toolCallId, answer: '', wasFreeform: false, dismissed: true},
                  'ask_user_response',
                )
              }
            >
              Skip
            </Button>
          </>
        }
      >
        {choices.length > 0 ? (
          <ChoiceButtonGroup aria-label="Answer choices" autoFocus={autoFocus && !isDisabled}>
            {choices.map((choice, index) => (
              <ChoiceButton
                key={choice}
                number={index + 1}
                label={choice}
                ariaLabel={choice}
                disabled={isDisabled}
                onClick={() =>
                  respond({promptId: prompt.toolCallId, answer: choice, wasFreeform: false}, 'ask_user_response')
                }
              />
            ))}
            <ChoiceButtonInput
              number={choices.length + 1}
              ariaLabel="Answer"
              placeholder="Type your answer..."
              value={freeform}
              onChange={setFreeform}
              onSubmit={submitFreeform}
              disabled={isDisabled}
              autoFocus={false}
            />
          </ChoiceButtonGroup>
        ) : (
          <TextInput
            aria-label="Answer"
            placeholder="Type your answer..."
            value={freeform}
            onChange={event => setFreeform(event.target.value)}
            onKeyDown={event => {
              // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
              if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
                event.preventDefault()
                submitFreeform()
              }
            }}
            disabled={isDisabled}
            block
            autoFocus={autoFocus && !isDisabled}
          />
        )}
      </UserChoiceCard>
    </div>
  )
}

EstablishedAhpSessionPrompt.displayName = 'EstablishedAhpSessionPrompt'
